import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Users, QrCode, Share2, Copy, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { QRCodeSVG } from "qrcode.react";

interface EventParticipationDialogProps {
  eventName?: string;
  triggerClassName?: string;
  triggerVariant?: "default" | "outline" | "ghost" | "hero";
  showQrInTrigger?: boolean;
  externalOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

export function EventParticipationDialog({
  eventName: initialEventName = "",
  triggerClassName = "",
  triggerVariant = "default",
  showQrInTrigger = false,
  externalOpen,
  onOpenChange,
  hideTrigger = false,
}: EventParticipationDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [eventName, setEventName] = useState(initialEventName);
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const { toast } = useToast();

  // Support both controlled and uncontrolled modes
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (value: boolean) => {
    if (onOpenChange) {
      onOpenChange(value);
    }
    setInternalOpen(value);
  };

  // Update event name when prop changes
  useEffect(() => {
    if (initialEventName) {
      setEventName(initialEventName);
    }
  }, [initialEventName]);

  // Generate the shareable URL for this form - opens dialog directly
  const shareableUrl = typeof window !== "undefined" 
    ? `${window.location.origin}/forms?participate=true${eventName ? `&event=${encodeURIComponent(eventName)}` : ""}`
    : "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    if (!eventName.trim()) {
      toast({
        title: "Event name required",
        description: "Please enter the event name.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Register participation
      const { error: participationError } = await supabase
        .from("event_participants")
        .insert({
          email: email.trim(),
          name: name.trim() || null,
          event_name: eventName.trim(),
        });

      if (participationError) {
        if (participationError.code === "23505") {
          toast({
            title: "Already registered",
            description: "You're already registered for this event!",
          });
          setIsSuccess(true);
        } else {
          throw participationError;
        }
      } else {
        // Also add to subscribers if opted in
        if (subscribeToNewsletter) {
          await supabase
            .from("subscribers")
            .insert({ email: email.trim(), name: name.trim() || null })
            .select();
        }

        setIsSuccess(true);
        toast({
          title: "You're in! 🎉",
          description: `We've confirmed your participation for ${eventName}.`,
        });

        setEmail("");
        setName("");
      }
    } catch (error) {
      console.error("Participation error:", error);
      toast({
        title: "Error",
        description: "Failed to register. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareableUrl);
    toast({
      title: "Link copied!",
      description: "Share this link with others to invite them.",
    });
  };

  const resetForm = () => {
    setIsSuccess(false);
    setEmail("");
    setName("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { resetForm(); setShowQrCode(false); } }}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant={triggerVariant} className={triggerClassName}>
            <CalendarCheck className="w-4 h-4 mr-2" />
            I Will Participate
            {showQrInTrigger && <QrCode className="w-4 h-4 ml-2" />}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CalendarCheck className="w-5 h-5 text-accent" />
            Quick Event Registration
          </DialogTitle>
        </DialogHeader>

        {isSuccess ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <div className="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-accent" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              You're Registered!
            </h3>
            <p className="text-muted-foreground text-sm mb-4">
              We'll send event details to your email.
            </p>
            <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
              Close
            </Button>
          </motion.div>
        ) : showQrCode ? (
          <div className="space-y-4">
            <div className="flex flex-col items-center p-6 bg-white rounded-xl">
              <QRCodeSVG 
                value={shareableUrl} 
                size={200}
                level="H"
                includeMargin
              />
            </div>
            <p className="text-center text-sm text-muted-foreground">
              Scan this QR code to register for the event
            </p>
            <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
              <Input 
                value={shareableUrl} 
                readOnly 
                className="text-xs h-8"
              />
              <Button size="sm" variant="outline" onClick={copyLink}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => setShowQrCode(false)}
            >
              Back to Form
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="event-name" className="text-sm">
                  Event Name *
                </Label>
                <Input
                  id="event-name"
                  type="text"
                  placeholder="e.g., Business Seminar 2026"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  required
                  className="h-10"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="participation-name" className="text-sm">
                  Your Name (optional)
                </Label>
                <Input
                  id="participation-name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="participation-email" className="text-sm">
                  Email *
                </Label>
                <Input
                  id="participation-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-10"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="subscribe-newsletter-dialog"
                  checked={subscribeToNewsletter}
                  onCheckedChange={(checked) =>
                    setSubscribeToNewsletter(checked as boolean)
                  }
                />
                <Label
                  htmlFor="subscribe-newsletter-dialog"
                  className="text-xs text-muted-foreground cursor-pointer"
                >
                  Subscribe to newsletter for updates
                </Label>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <Users className="w-4 h-4 mr-2" />
                    Confirm Participation
                  </>
                )}
              </Button>
            </form>

            <div className="pt-2 border-t border-border">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowQrCode(true)}
              >
                <QrCode className="w-4 h-4 mr-2" />
                Share via QR Code
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
